import { useEffect, useMemo } from 'react';
import { Box, Drawer, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { RxCross2 } from 'react-icons/rx';
import { useForm } from 'react-hook-form';
import Select from 'components/Common/Select';
import Button from 'components/Common/Button';
import { useGetBusinesses } from 'apis/agency/hook';
import { useCreateTaggings, useGetTaggingContexts } from '@/apis/agency/taggings/hook';
import { buildChurnSignalSelectOptions } from './businessTags';

interface FormValues {
  business_id: number | '';
  signal_types: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export default function AddChurnSignalsDrawer({ open, onClose }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: { business_id: '', signal_types: [] }
  });

  const { data: businesses, isLoading: businessesLoading } = useGetBusinesses({
    per_page: 9999,
    filter: { active_and_past_due: true }
  });

  const { data: contexts, isLoading: contextsLoading } = useGetTaggingContexts();

  const { mutate: createTags, isPending } = useCreateTaggings(() => {
    reset();
    onClose();
  });

  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const businessOptions =
    businesses?.data?.map((b) => ({
      value: b.id,
      label: b.name
    })) ?? [];

  const signalOptions = useMemo(() => buildChurnSignalSelectOptions(contexts), [contexts]);

  const onSubmit = (values: FormValues) => {
    if (!values.business_id || !values.signal_types.length) return;

    createTags({
      tagging: {
        taggable_type: 'Business',
        taggable_id: Number(values.business_id),
        tags: values.signal_types.map((name) => ({ name, context: 'churn_signal' }))
      }
    });
  };

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 480,
          maxWidth: '100%',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={2.5}
        py={2}
        borderBottom='1px solid #ECECEC'
      >
        <Typography fontSize={20} fontWeight={500}>
          Add Signals to Business
        </Typography>
        <IconButton size='small' onClick={onClose} aria-label='Close'>
          <RxCross2 />
        </IconButton>
      </Box>

      <Box flex={1} overflow='auto' px={2.5} py={2.5}>
        <Box
          component='form'
          id='add-churn-signals-form'
          onSubmit={handleSubmit(onSubmit)}
          display='flex'
          flexDirection='column'
          gap={2.5}
        >
          <Select
            name='business_id'
            control={control}
            label='Business Name'
            options={businessOptions}
            searchable
            searchPlaceholder='Search businesses'
            isLoading={businessesLoading}
            rules={{ required: 'Business is required' }}
            fullWidth
          />

          <Select
            name='signal_types'
            control={control}
            label='Churn Signals'
            options={signalOptions}
            multiple
            isLoading={contextsLoading}
            rules={{ required: 'At least one signal is required' }}
            fullWidth
          />
        </Box>
      </Box>

      <Box px={2.5} py={2} borderTop='1px solid #ECECEC' mt='auto'>
        <Stack direction='row' justifyContent='flex-end' spacing={1.5}>
          <Button variant='outlined' onClick={onClose} disabled={isPending} sx={{ borderRadius: '18px' }}>
            Cancel
          </Button>
          <Button
            type='submit'
            form='add-churn-signals-form'
            variant='contained'
            disabled={isPending}
            sx={{ borderRadius: '18px' }}
          >
            Save
          </Button>
        </Stack>
      </Box>
    </Drawer>
  );
}
